// src/services/SubscriptionService.js
import { EventEmitter } from "events";
import { fetchStripeCatalogProducts } from "../config/stripeCatalog";

/**
 * SubscriptionOverviewPage state: Stripe verification + catalog fetch.
 * React Native WebView is only used for push-token sync (see sendMessage).
 */
class SubscriptionService extends EventEmitter {
  constructor() {
    super();
    this.products = [];
    this.customerInfo = null;
    this.isSubscribed = false;
    this.isLoading = false;
    this.isInitialized = false;

    this.sendMessage = this.sendMessage.bind(this);
    this.handleWrapperResponse = this.handleWrapperResponse.bind(this);
    this.setupMessageListener();
  }

  static getInstance() {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  setupMessageListener() {
    if (typeof window !== "undefined") {
      window.addEventListener("wrapperResponse", this.handleWrapperResponse);
    }
  }

  handleWrapperResponse(event) {
    const response = event.detail;
    this.emit(`response_${response.requestId}`, response);
  }

  async sendMessage(action, data = {}) {
    return new Promise((resolve, reject) => {
      const requestId = Date.now().toString();
      const message = {
        type: "REQUEST",
        action,
        requestId,
        data,
      };

      if (typeof window !== "undefined" && window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify(message));

        const handleResponse = (response) => {
          this.removeListener(`response_${requestId}`, handleResponse);
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.error || "Request failed"));
          }
        };

        this.once(`response_${requestId}`, handleResponse);

        setTimeout(() => {
          this.removeListener(`response_${requestId}`, handleResponse);
          reject(new Error("Request timeout"));
        }, 10000);
      } else {
        reject(new Error("Not running in app wrapper"));
      }
    });
  }

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.checkEntitlements().catch(() => {});
      await this.getProducts().catch(() => {});
    } catch (error) {
      this.emit("error", error);
    } finally {
      this.isInitialized = true;
      this.emit("initialized");
    }
  }

  async getProducts() {
    try {
      this.setLoading(true);
      this.products = await fetchStripeCatalogProducts();
      this.emit("productsUpdated", this.products);
      return this.products;
    } catch (error) {
      this.emit("error", error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /** @deprecated No native purchaser; returns null. */
  async getCustomerInfo() {
    return null;
  }

  async checkEntitlements() {
    try {
      const { auth } = await import("../config/firebase");
      const uid = auth?.currentUser?.uid;
      if (!uid) {
        const data = { hasActiveEntitlements: false };
        this.setSubscriptionStatus(false);
        this.emit("entitlementsChecked", data);
        return data;
      }

      const SubscriptionVerificationService = (
        await import("./SubscriptionVerificationService")
      ).default;
      const v = await SubscriptionVerificationService.verifySubscription({
        uid,
      });
      const hasActive = v.isValid === true;
      this.setSubscriptionStatus(hasActive);
      const data = { hasActiveEntitlements: hasActive };
      this.emit("entitlementsChecked", data);
      return data;
    } catch (error) {
      this.emit("error", error);
      throw error;
    }
  }

  async purchaseProduct() {
    throw new Error(
      "Use Stripe checkout in the app (subscribe / checkout page), not native purchase."
    );
  }

  async restorePurchases() {
    try {
      this.setLoading(true);
      const { auth } = await import("../config/firebase");
      const uid = auth?.currentUser?.uid;
      if (uid) {
        const SubscriptionVerificationService = (
          await import("./SubscriptionVerificationService")
        ).default;
        const v = await SubscriptionVerificationService.verifySubscription({
          uid,
        });
        this.setSubscriptionStatus(v.isValid === true);
      }
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  async manageSubscriptions() {
    try {
      this.setLoading(true);
      if (typeof window !== "undefined" && window.location?.href) {
        try {
          const { getFunctions, httpsCallable } =
            await import("firebase/functions");
          const { app } = await import("../config/firebase");
          const returnUrl = `${window.location.origin}${window.location.pathname}`;
          const fn = httpsCallable(
            getFunctions(app, "europe-west1"),
            "createBillingPortalForUser"
          );
          const result = await fn({ return_url: returnUrl });
          const url = result.data?.url;
          if (url && typeof url === "string") {
            window.location.assign(url);
            return { opened: true, url };
          }
        } catch (portalErr) {
          console.warn(
            "Stripe billing portal failed, trying app wrapper:",
            portalErr
          );
        }
      }

      const response = await this.sendMessage("MANAGE_SUBSCRIPTIONS");
      this.emit("subscriptionManagementOpened", response.data);
      return response.data;
    } catch (error) {
      this.emit("error", error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async handlePostPurchaseActions(_customerInfo = null, options = {}) {
    try {
      let uid = options.uid;
      let email = options.email;

      if (!uid) {
        const { auth } = await import("../config/firebase");
        const currentUser = auth?.currentUser;
        if (!currentUser) {
          return { success: false, error: "No authenticated user" };
        }
        uid = currentUser.uid;
        email = email || currentUser.email;
      }

      const { createTeamIfNeeded } =
        await import("../utils/teams/postPurchaseTeamCreation");
      await createTeamIfNeeded(uid, email);
      return { success: true };
    } catch (error) {
      console.error("handlePostPurchaseActions:", error);
      return { success: false, error: error.message };
    }
  }

  updateSubscriptionStatus() {
    /* isSubscribed is driven by checkEntitlements / restorePurchases */
  }

  setSubscriptionStatus(isSubscribed) {
    if (this.isSubscribed !== isSubscribed) {
      this.isSubscribed = isSubscribed;
      this.emit("subscriptionStatusChanged", isSubscribed);
    }
  }

  setLoading(isLoading) {
    if (this.isLoading !== isLoading) {
      this.isLoading = isLoading;
      this.emit("loadingChanged", isLoading);
    }
  }

  getState() {
    return {
      products: this.products,
      customerInfo: this.customerInfo,
      isSubscribed: this.isSubscribed,
      isLoading: this.isLoading,
      isInitialized: this.isInitialized,
    };
  }

  reset() {
    this.products = [];
    this.customerInfo = null;
    this.isSubscribed = false;
    this.isLoading = false;
    this.isInitialized = false;
    this.emit("reset");
  }

  destroy() {
    if (typeof window !== "undefined") {
      window.removeEventListener("wrapperResponse", this.handleWrapperResponse);
    }
    this.removeAllListeners();
  }
}

export default SubscriptionService.getInstance();
